import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	IDrawerDataListColumn,
	Loading,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { campaignService } from "../shared/api/campaignService";
import { createCampaignUrlService } from "../shared/api/createCampaignUrlService";
import { useDetailsPageEntityLoader } from "../shared/hooks";
import { ICampaignUrlIndexResource } from "../shared/types";
import { CampaignUrlCreateDrawer, CampaignUrlUpdateDrawer } from "./CampaignUrlDrawer";

export const CampaignUrlList: FC = () => {
	const { id = "" } = useParams();
	const { t } = useTranslation();
	const campaignUrlService = useMemo(() => createCampaignUrlService(id), [id]);
	const { data: campaign } = useDetailsPageEntityLoader(campaignService);
	const crudOperations = useLaravelCrudEntity(campaignUrlService, {
		entityName: t("Campaign URL"),
		messages: {
			create: t("Campaign URL was successfully created."),
			update: t("Campaign URL was successfully updated."),
			delete: t("Campaign URL was successfully deleted."),
		},
	});
	const canCreate = useHasPermission<PermissionName>(["campaignUrl.create"]);
	const canUpdate = useHasPermission<PermissionName>(["campaignUrl.update"]);

	const dataListColumns: IDrawerDataListColumn<ICampaignUrlIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Slug"),
				valueKey: "slug",
				type: "value",
			},
			{
				heading: t("Target URL"),
				type: "value",
				valueKey: "targetUrl",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit campaign URL")}
							label={t("Edit campaign URL")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(id)}
							title={t("Delete campaign URL")}
							label={t("Delete campaign URL")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this campaign URL?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t],
	);

	if (!campaign) {
		return <Loading />;
	}

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(campaignUrlService.listKey, campaignUrlService.index)}
			renderCreateDrawer={(drawerProps) => (
				<CampaignUrlCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<CampaignUrlUpdateDrawer
					{...drawerProps}
					service={campaignUrlService}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			translations={{
				title: t("Campaign URLs of {{campaign}}", { campaign: campaign.name }),
				empty: t("No campaign URLs available"),
			}}
			initialSearch={{ search: "", searchFields: [{ name: "slug", label: t("Slug") }] }}
		/>
	);
};
